// components/Dashboard.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompetition } from '../contexts/CompetitionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Plus, Users, Calendar, MapPin, Clock, Trophy, Trash2, AlertTriangle, X, TimerIcon } from 'lucide-react';
import { deleteCompetitions } from '../services/apiServices';

export const Dashboard = () => {
    const navigate = useNavigate();
    const { loadCompetitions, competitions, loading } = useCompetition();
    const [deletingId, setDeletingId] = useState(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [competitionToDelete, setCompetitionToDelete] = useState(null);

    useEffect(() => {
        let organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo")) ? JSON.parse(localStorage.getItem("loggedAdminInfo")).organizerId : "";
        loadCompetitions(organiserId);
    }, [loadCompetitions]);

    const handleDeleteClick = (competition, event) => {
        event.stopPropagation(); // Prevent navigation when clicking delete
        setCompetitionToDelete(competition);
        setShowDeleteDialog(true);
    };

    const handleConfirmDelete = async () => {
        if (!competitionToDelete) return;

        setDeletingId(competitionToDelete._id);
        try {
            const organiserId = JSON.parse(localStorage.getItem("loggedAdminInfo"))?.organizerId;
            if (!organiserId) {
                alert('Organizer ID not found');
                return;
            }

            const response = await deleteCompetitions(organiserId, competitionToDelete._id);

            if (response && response.ok) {
                // Reload the competitions list
                await loadCompetitions(organiserId);
                // Removed the alert since we'll show success state in the UI
            } else {
                throw new Error('Failed to delete competition');
            }
        } catch (error) {
            console.error('Error deleting competition:', error);
            alert('Failed to delete competition. Please try again.');
        } finally {
            setDeletingId(null);
            setShowDeleteDialog(false);
            setCompetitionToDelete(null);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteDialog(false);
        setCompetitionToDelete(null);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'setup': return 'bg-gray-500';
            case 'ready': return 'bg-blue-500';
            case 'countdown': return 'bg-blue-500';
            case 'running': return 'bg-green-500';
            case 'paused': return 'bg-yellow-500';
            case 'completed': return 'bg-purple-500';
            case 'active': return 'bg-green-500';
            case 'upcoming': return 'bg-blue-500';
            default: return 'bg-gray-500';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'setup': return 'SETUP';
            case 'ready': return 'READY';
            case 'countdown': return 'COUNTDOWN';
            case 'running': return 'RUNNING';
            case 'paused': return 'PAUSED';
            case 'completed': return 'COMPLETED';
            case 'active': return 'ACTIVE';
            case 'upcoming': return 'UPCOMING';
            default: return status.toUpperCase();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            return dateString;
        }
    };

    const getJudgesAssigned = (comp) => {
        const totalBoulders = comp.numberOfBoulders || comp.boulders?.length || 0;
        let uniqueJudges = new Set();

        if (comp.boulders && Array.isArray(comp.boulders)) {
            comp.boulders.forEach(boulder => {
                if (boulder.judgeId && boulder.judgeId !== '') {
                    uniqueJudges.add(boulder.judgeId);
                }
            });
        }

        const assignedJudgesCount = uniqueJudges.size;
        return `${assignedJudgesCount}/${totalBoulders}`;
    };

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen p-4 md:p-6 max-w-6xl mx-auto">
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600">Loading competitions...</p>
                    </div>
                </div>
            </div>
        );
    }

    // No competitions state
    if (competitions.length === 0) {
        return (
            <div className="min-h-screen p-4 md:p-6 max-w-6xl mx-auto">
                <div className="space-y-6">
                    {/* Header with Create Button */}
                    <div className="flex justify-between items-center">
                        <div>
                            <h1 className="text-3xl font-bold">Competitions</h1>
                            <p className="text-gray-600">Manage your bouldering competitions</p>
                        </div>
                        <Button
                            onClick={() => navigate('/create-round')}
                            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium "
                        >
                            <Plus className="size-4" />
                            Create Competition
                        </Button>
                    </div>

                    {/* Empty State */}
                    <Card className="border-2 border-dashed border-gray-300">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <Trophy className="size-16 text-gray-400 mb-4" />
                            <h3 className="text-xl font-semibold text-gray-600 mb-2">No competitions yet</h3>
                            <p className="text-gray-500 text-center mb-6">
                                Get started by creating your first bouldering competition
                            </p>
                            <Button
                                onClick={() => navigate('/create-round')}
                                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                            >
                                <Plus className="size-4" />
                                Create Your First Competition
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-4 md:p-6 max-w-6xl mx-auto">
            {/* Improved Delete Confirmation Dialog */}
            <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
                <DialogContent className="max-w-md bg-white border-0 shadow-2xl rounded-xl">
                    <DialogHeader className="text-center space-y-4 pb-4">
                        <div className="mx-auto flex items-center justify-center size-12 bg-red-100 rounded-full">
                            <AlertTriangle className="size-6 text-red-600" />
                        </div>
                        <DialogTitle className="text-xl font-bold text-gray-900">
                            Delete Competition
                        </DialogTitle>
                        <DialogDescription className="text-gray-600 text-base">
                            This action cannot be undone. This will permanently delete the competition and all associated data.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-start gap-3">
                            <div className="flex-1">
                                <p className="font-semibold text-red-800 text-lg">
                                    {competitionToDelete?.name}
                                </p>
                                <div className="mt-2 space-y-1 text-sm text-red-700">
                                    <p className="flex items-center gap-2">
                                        <Users className="size-4" />
                                        {competitionToDelete?.competitors?.length || 0} competitors
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <Trophy className="size-4" />
                                        {competitionToDelete?.numberOfBoulders || 0} boulders
                                    </p>
                                    <p className="flex items-center gap-2">
                                        <Calendar className="size-4" />
                                        {formatDate(competitionToDelete?.startDate)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Alert className="bg-amber-50 border-amber-200 mb-6">
                        <AlertTriangle className="size-4 text-amber-600" />
                        <AlertDescription className="text-amber-700">
                            All competition data including climbers, boulders, judges, and results will be permanently deleted.
                        </AlertDescription>
                    </Alert>

                    <DialogFooter className="flex gap-3 sm:gap-3">
                        <Button
                            variant="outline"
                            onClick={handleCancelDelete}
                            disabled={deletingId}
                            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-medium h-11"
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deletingId}
                            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-medium h-11 shadow-sm"
                        >
                            {deletingId ? (
                                <div className="flex items-center gap-2">
                                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                    Deleting...
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Trash2 className="size-4" />
                                    Delete Competition
                                </div>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Competitions</h1>
                        <p className="text-gray-600">Manage your bouldering competitions</p>
                    </div>
                    <Button
                        onClick={() => navigate('/multi-timer')}
                        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-medium"
                    >
                        <TimerIcon className="size-4" />
                        Multi-Competition Timer
                    </Button>
                    <Button
                        onClick={() => navigate('/create-round')}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium"
                    >
                        <Plus className="size-4" />
                        Create Competition
                    </Button>
                </div>

                {/* Competitions List */}
                <Card className="shadow-sm border">
                    <CardHeader>
                        <CardTitle className="text-xl font-semibold">Your Competitions ({competitions.length})</CardTitle>
                        <CardDescription className="text-gray-500">
                            Select a competition to manage or view details
                        </CardDescription>
                    </CardHeader>

                    <CardContent className="p-0">
                        <div className="divide-y">
                            {competitions?.length > 0 && competitions.map((comp) => (
                                <div
                                    key={comp.id}
                                    className="flex items-center justify-between p-6 transition-all hover:bg-gray-50 cursor-pointer group relative"
                                    onClick={() => navigate(`/competitiondetails`, { state: comp })}
                                >
                                    {/* LEFT SECTION */}
                                    <div className="flex items-start gap-4 flex-1">
                                        {/* Icon */}
                                        <div className="bg-blue-100 p-3 rounded-xl shadow-sm">
                                            <Trophy className="size-6 text-blue-600" />
                                        </div>

                                        {/* Info */}
                                        <div className="flex-1">
                                            {/* Title + Status */}
                                            <div className="flex items-center gap-3 mb-1">
                                                <h3 className="text-lg font-semibold truncate max-w-[240px]">
                                                    {comp.name}
                                                </h3>
                                                <Badge
                                                    className={`text-xs px-2 py-0.5 rounded-md ${getStatusColor(
                                                        comp.status
                                                    )}`}
                                                >
                                                    {getStatusText(comp.status)}
                                                </Badge>
                                            </div>

                                            {/* Details */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm text-gray-700 mt-2">
                                                {/* <div className="flex items-center gap-2">
                                                    <MapPin className="size-4 text-gray-500" />
                                                    <span>{comp.location || 'No location'}</span>
                                                </div> */}

                                                <div className="flex items-center gap-2">
                                                    <Calendar className="size-4 text-gray-500" />
                                                    <span>{formatDate(comp.startDate)}</span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <Users className="size-4 text-gray-500" />
                                                    <span>{comp.competitors?.length || 0} competitors</span>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <Clock className="size-4 text-gray-500" />
                                                    <span>{comp.climbTime}s climb time</span>
                                                </div>
                                            </div>

                                            {/* Category */}
                                            {comp.category && (
                                                <p className="text-sm text-gray-500 mt-2">
                                                    Category: {comp.category}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* RIGHT SECTION */}
                                    <div className="text-right min-w-[150px]">
                                        <div className="grid grid-cols-2 gap-6 text-sm mb-3">
                                            <div>
                                                <p className="text-gray-500">Boulders</p>
                                                <p className="font-semibold text-gray-900">{comp.numberOfBoulders}</p>
                                            </div>

                                            <div>
                                                <p className="text-gray-500">Judges</p>
                                                <p className="font-semibold text-gray-900">{getJudgesAssigned(comp)}</p>
                                            </div>
                                        </div>

                                        <div className="flex items-center gap-2 justify-end">
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                className="opacity-0 group-hover:opacity-100 transition-opacity"
                                            >
                                                View Details
                                            </Button>

                                            {/* Simple Delete Button */}
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="opacity-0 group-hover:opacity-100 transition-opacity text-red-600 hover:text-red-700 hover:bg-red-50"
                                                onClick={(e) => handleDeleteClick(comp, e)}
                                                disabled={deletingId === comp.id}
                                            >
                                                {deletingId === comp.id ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                ) : (
                                                    <Trash2 className="size-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};